import * as Dialog from '@radix-ui/react-dialog';
import { LogOut, PanelLeftClose, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNotificationsSummary } from '../contexts/NotificationsContext';
import { cn } from '../lib/cn';
import { appNavigationItems } from './appNavigation';
import AppAvatar from './ui/AppAvatar';

function WorkspaceSummary({ currentUser }) {
  return (
    <div className="min-w-0 rounded-[26px] border border-app-border/80 bg-white/80 p-5 shadow-subtle backdrop-blur-sm">
      <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.18em] text-app-muted">
        Shared workspace
      </p>
      <p className="mt-3 text-lg font-semibold tracking-tight text-app-ink">Family Copilot</p>
      <p className="mt-2 text-sm leading-6 text-app-muted">
        Collections, tasks, calendar, and history in one persistent family workspace.
      </p>
      <div className="mt-5 flex min-w-0 items-center gap-3 rounded-[20px] border border-app-border/70 bg-app-surface-alt/85 px-4 py-3">
        <AppAvatar
          email={currentUser?.email}
          size="md"
          className="border-app-border/70 bg-white"
        />
        <div className="min-w-0">
          <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
            Signed in
          </p>
          <p className="m-0 truncate text-sm font-medium text-app-ink">
            {currentUser?.email || 'User'}
          </p>
        </div>
      </div>
    </div>
  );
}

function NavLinks({ pathname, navigate, onNavigate }) {
  const { unreadCount } = useNotificationsSummary();

  return (
    <div className="grid gap-3">
      {appNavigationItems.map((item) => {
        const active = item.matches(pathname);
        const badgeValue = item.path === '/notifications' && unreadCount ? unreadCount : 0;

        return (
          <button
            key={item.path}
            className={cn(
              'group grid w-full grid-cols-[48px_minmax(0,1fr)] items-center gap-4 rounded-[22px] border px-4 py-4 text-left transition',
              active
                ? 'border-app-accent/20 bg-app-accent/10 text-app-ink shadow-subtle'
                : 'border-transparent bg-transparent text-app-muted hover:border-app-border/80 hover:bg-white/75 hover:text-app-ink'
            )}
            onClick={() => {
              navigate(item.path);
              onNavigate?.();
            }}
            title={item.label}
            type="button"
          >
            <span
              className={cn(
                'inline-flex h-12 w-12 items-center justify-center rounded-[18px] transition',
                active
                  ? 'bg-app-accent text-white'
                  : 'bg-app-surface-alt text-app-muted group-hover:bg-app-accent/10 group-hover:text-app-accent-strong'
              )}
            >
              <item.icon className="h-5 w-5" aria-hidden="true" />
            </span>

            <span className="min-w-0">
              <span className="flex items-center gap-2">
                <span
                  className={cn(
                    'block truncate text-sm font-semibold',
                    active ? 'text-app-ink' : 'text-app-ink group-hover:text-app-ink'
                  )}
                >
                  {item.label}
                </span>
                {badgeValue ? (
                  <span className="inline-flex min-w-[1.35rem] items-center justify-center rounded-full bg-app-accent px-1.5 py-0.5 text-[11px] font-bold text-white">
                    {badgeValue > 99 ? '99+' : badgeValue}
                  </span>
                ) : null}
              </span>
              <span
                className={cn(
                  'mt-0.5 block truncate text-xs',
                  active ? 'text-app-muted' : 'text-app-muted'
                )}
              >
                {item.description}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function LogoutButton({ onLogout }) {
  return (
    <button
      className="grid w-full grid-cols-[48px_minmax(0,1fr)] items-center gap-4 rounded-[22px] border border-red-200/70 bg-red-50 px-4 py-4 text-left transition hover:border-red-300 hover:bg-red-100/80"
      onClick={onLogout}
      title="Logout"
      type="button"
    >
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-[18px] bg-white text-red-500 shadow-subtle">
        <LogOut className="h-5 w-5" aria-hidden="true" />
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold text-red-700">Logout</span>
        <span className="mt-0.5 block truncate text-xs text-red-600/80">Clear the saved local session</span>
      </span>
    </button>
  );
}

function SidebarNav({ currentUser, onLogout, isMobileNavOpen, onMobileNavOpenChange }) {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <>
      <nav className="fixed left-0 top-0 hidden h-screen w-sidebar grid-rows-[auto_1fr_auto] gap-8 overflow-x-hidden border-r border-app-border/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(248,250,252,0.98))] px-5 py-6 text-app-ink shadow-panel md:grid">
        <div className="flex items-center justify-between rounded-[26px] border border-app-border/80 bg-white/80 px-5 py-5 shadow-subtle backdrop-blur-sm">
          <div>
            <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.18em] text-app-muted">
              Workspace
            </p>
            <h1 className="mt-1 text-xl font-semibold tracking-tight text-app-ink">
              Family Copilot
            </h1>
          </div>
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-[18px] border border-app-border/70 bg-app-surface-alt text-app-muted">
            <PanelLeftClose className="h-5 w-5" aria-hidden="true" />
          </span>
        </div>

        <div className="grid min-w-0 content-start gap-8 overflow-y-auto overflow-x-hidden pr-2">
          <WorkspaceSummary currentUser={currentUser} />

          <div>
            <p className="mb-4 px-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-app-muted">
              Navigation
            </p>
            <NavLinks pathname={location.pathname} navigate={navigate} />
          </div>
        </div>

        <div className="border-t border-app-border/80 pt-6">
          <LogoutButton onLogout={onLogout} />
        </div>
      </nav>

      <Dialog.Root open={isMobileNavOpen} onOpenChange={onMobileNavOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-[70] bg-slate-950/35 backdrop-blur-sm md:hidden" />
          <Dialog.Content className="fixed left-0 top-0 z-[80] flex h-screen w-[min(88vw,380px)] flex-col gap-6 overflow-x-hidden border-r border-app-border/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.99),rgba(248,250,252,0.99))] px-5 py-6 text-app-ink shadow-panel outline-none md:hidden">
            <div className="flex items-center justify-between rounded-[26px] border border-app-border/80 bg-white/85 px-5 py-5 shadow-subtle backdrop-blur-sm">
              <div>
                <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.18em] text-app-muted">
                  Menu
                </p>
                <Dialog.Title className="mt-1 text-xl font-semibold tracking-tight text-app-ink">
                  Family Copilot
                </Dialog.Title>
              </div>
              <Dialog.Close asChild>
                <button
                  className="inline-flex h-12 w-12 items-center justify-center rounded-[18px] border border-app-border/70 bg-app-surface-alt text-app-muted"
                  type="button"
                  aria-label="Close menu"
                >
                  <X className="h-5 w-5" aria-hidden="true" />
                </button>
              </Dialog.Close>
            </div>

            <WorkspaceSummary currentUser={currentUser} />

            <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
              <p className="mb-4 px-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-app-muted">
                Navigation
              </p>
              <NavLinks
                pathname={location.pathname}
                navigate={navigate}
                onNavigate={() => onMobileNavOpenChange(false)}
              />
            </div>

            <div className="border-t border-app-border/80 pt-6">
              <LogoutButton onLogout={onLogout} />
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

export default SidebarNav;
